import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, PageHeader, Button, Input } from "@/components/ui";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, KeyRound, Server, Clock, ScrollText, Check, AlertTriangle,
  Smartphone, Database, Cloud, RefreshCw, FileCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const IMPLEMENTED = [
  { icon: Lock, title: "Encryption in transit & at rest", text: "TLS 1.2+ everywhere; data encrypted at rest by the hosting platform (Aptible/AWS KMS)." },
  { icon: KeyRound, title: "Role-based access control", text: "Unique user IDs, least-privilege roles (admin / care manager / provider / member), server-enforced." },
  { icon: Smartphone, title: "Multi-factor authentication (TOTP)", text: "Optional per-user 2FA via any authenticator app — enroll below." },
  { icon: Clock, title: "Automatic logoff", text: "Idle sessions end after 15 minutes (HIPAA §164.312(a)(2)(iii))." },
  { icon: ScrollText, title: "PHI-access audit logging", text: "Every member-record view is logged with user, time, and record (HIPAA §164.312(b))." },
  { icon: Database, title: "Tenant data isolation", text: "Each MCO's data is fully partitioned — no cross-tenant access, ever." },
  { icon: Cloud, title: "Encrypted, automated backups", text: "Managed daily backups with point-in-time recovery via the hosting platform." },
  { icon: ShieldCheck, title: "U.S.-onshore, no offshore PHI", text: "All processing and support stays within the United States." },
];

const IN_PROGRESS = [
  { title: "SOC 2 Type II", text: "Observation period underway; report expected before go-live. Interim Type I / bridge letter available." },
  { title: "Signed BAAs with all subprocessors", text: "Hosting (Aptible/AWS), email, and the AI/LLM provider — executed before any real PHI is processed." },
  { title: "Independent penetration test", text: "Third-party test scheduled; summary letter shareable under NDA." },
  { title: "Formal risk analysis & policies", text: "Written HIPAA policies, risk assessment, and 6-year retention — being finalized with counsel." },
  { title: "Named Security & Privacy Officer", text: "Designated officers and workforce training program on award." },
];

const WHATS_LEFT = [
  "Execute a signed BAA with the hosting platform (Aptible today; portable to AWS or the MCO's environment).",
  "Execute BAAs with every subprocessor that can touch PHI — especially the AI/LLM provider — or de-identify data sent to models.",
  "Complete the SOC 2 Type II observation period and obtain the report (Aptible's controls & evidence shorten this).",
  "Commission an independent penetration test and remediate findings.",
  "Publish written HIPAA policies, complete a formal risk analysis, and stand up workforce training.",
  "Formally designate a Security Officer and a Privacy Officer.",
  "Sign the BAA with Trillium (or the contracting MCO) at contract.",
];

function MfaCard() {
  const [enabled, setEnabled] = useState(null);
  const [phase, setPhase] = useState("view"); // view | setup | disable
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/security/mfa/status").then((r) => setEnabled(r.data.mfa_enabled)).catch(() => setEnabled(false));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const r = await api.post("/security/mfa/setup"); setQr(r.data.qr_data_url); setPhase("setup"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const enable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/security/mfa/enable", { code }); toast.success("MFA enabled"); setPhase("view"); setCode(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/security/mfa/disable", { password: pw, code }); toast.success("MFA disabled"); setPhase("view"); setPw(""); setCode(""); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid="mfa-card">
      <div className="flex items-center gap-2 mb-1"><Smartphone className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Two-factor authentication</h2></div>
      <p className="text-sm text-muted-foreground mb-4">Add a second factor to your own account with any authenticator app (Google Authenticator, Authy, 1Password).</p>

      {enabled === null ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: enabled ? "hsl(152 55% 94%)" : "hsl(40 90% 95%)", color: enabled ? "hsl(152 60% 26%)" : "hsl(32 80% 34%)" }}
            data-testid="mfa-status-badge">
            {enabled ? <Check className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {enabled ? "Enabled" : "Not enabled"}
          </div>

          {phase === "view" && (
            enabled
              ? <div><Button variant="outline" onClick={() => setPhase("disable")} data-testid="mfa-start-disable">Disable MFA</Button></div>
              : <div><Button onClick={startSetup} disabled={busy} data-testid="mfa-start-setup"><KeyRound className="h-4 w-4" /> {busy ? "Preparing…" : "Enable MFA"}</Button></div>
          )}

          {phase === "setup" && (
            <form onSubmit={enable} className="space-y-3">
              <p className="text-sm text-foreground">1. Scan this QR code in your authenticator app:</p>
              {qr && <img src={qr} alt="Scan to enroll" className="h-40 w-40 rounded-md border border-border" data-testid="mfa-qr" />}
              <p className="text-sm text-foreground">2. Enter the 6-digit code it shows:</p>
              <Input className="max-w-[180px] text-center text-lg tracking-[0.4em]" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" data-testid="mfa-enable-code" autoFocus />
              <div className="flex gap-2">
                <Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-enable-submit">{busy ? "Enabling…" : "Confirm & enable"}</Button>
                <Button type="button" variant="ghost" onClick={() => { setPhase("view"); setCode(""); }}>Cancel</Button>
              </div>
            </form>
          )}

          {phase === "disable" && (
            <form onSubmit={disable} className="space-y-3 max-w-sm">
              <p className="text-sm text-foreground">Confirm your password and a current code to disable MFA.</p>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="mfa-disable-pw" />
              <Input className="text-center tracking-[0.4em]" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" data-testid="mfa-disable-code" />
              <div className="flex gap-2">
                <Button type="submit" variant="outline" disabled={busy} data-testid="mfa-disable-submit">{busy ? "Disabling…" : "Disable MFA"}</Button>
                <Button type="button" variant="ghost" onClick={() => { setPhase("view"); setPw(""); setCode(""); }}>Cancel</Button>
              </div>
            </form>
          )}
        </>
      )}
    </Card>
  );
}

function AuditCard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/security/audit-summary").then((r) => setData(r.data)).catch(() => setData({ total: 0, phi_access: 0, recent: [] }));
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-6" data-testid="audit-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><ScrollText className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">PHI-access audit trail</h2></div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="audit-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Every time a member's chart is opened, it is recorded — who, when, and which record.</p>
      {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-md border border-border p-3"><p className="text-2xl font-display font-bold text-brand-800" data-testid="audit-total">{data.total}</p><p className="text-xs text-muted-foreground">Total audited events</p></div>
            <div className="rounded-md border border-border p-3"><p className="text-2xl font-display font-bold text-brand-800" data-testid="audit-phi">{data.phi_access}</p><p className="text-xs text-muted-foreground">PHI record accesses</p></div>
          </div>
          <div className="space-y-1.5">
            {(data.recent || []).map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded border border-border px-3 py-1.5 text-xs" data-testid={`audit-row-${i}`}>
                <span className="text-foreground truncate">{r.user_email} · {r.action}{r.detail ? ` · ${r.detail}` : ""}</span>
                <span className="text-muted-foreground shrink-0">{(r.created_at || "").replace("T", " ").slice(0, 16)}</span>
              </div>
            ))}
            {(!data.recent || data.recent.length === 0) && <p className="text-xs text-muted-foreground">No access recorded yet — open a member chart to generate an entry.</p>}
          </div>
        </>
      )}
    </Card>
  );
}

export default function SecurityCompliance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="vc-fade-up" data-testid="security-compliance">
      <PageHeader title="Security & Compliance" subtitle="How Go MCO protects PHI and maps to HIPAA, SOC 2, and NIST 800-53 — with the safeguards live today and the roadmap to full attestation." />

      <Card className="p-6 mb-6 bg-brand-950 text-white border-0" data-testid="hosting-card">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="h-12 w-12 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><Server className="h-6 w-6 text-brand-300" /></div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold">HIPAA-eligible hosting under a signed BAA</h2>
            <p className="text-sm text-brand-100">Go MCO runs on <strong>Aptible</strong>, a HIPAA-compliant Platform-as-a-Service that provides encryption, isolation, audit logging, and encrypted backups out of the box — under a signed Business Associate Agreement. Because the platform is a standard containerized stack, it is <strong>portable to AWS</strong> (HIPAA-eligible) or deployable into the <strong>MCO's own environment</strong> when a plan prefers it.</p>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-6" data-testid="safeguards-implemented">
          <div className="flex items-center gap-2 mb-4"><Check className="h-5 w-5 text-indigo-600" /><h2 className="font-display text-xl font-bold">Safeguards live today</h2></div>
          <div className="space-y-3">
            {IMPLEMENTED.map((s) => (
              <div key={s.title} className="flex items-start gap-3">
                <s.icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" strokeWidth={1.75} />
                <div><p className="font-display font-semibold text-sm text-foreground">{s.title}</p><p className="text-xs text-muted-foreground">{s.text}</p></div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6" data-testid="safeguards-in-progress">
          <div className="flex items-center gap-2 mb-4"><Clock className="h-5 w-5 text-amber-600" /><h2 className="font-display text-xl font-bold">In progress → before go-live</h2></div>
          <div className="space-y-3">
            {IN_PROGRESS.map((s) => (
              <div key={s.title} className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
                <div><p className="font-display font-semibold text-sm text-foreground">{s.title}</p><p className="text-xs text-muted-foreground">{s.text}</p></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <MfaCard />
        {isAdmin ? <AuditCard /> : (
          <Card className="p-6" data-testid="audit-card-note">
            <div className="flex items-center gap-2 mb-1"><ScrollText className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">PHI-access audit trail</h2></div>
            <p className="text-sm text-muted-foreground">Every member-record view is logged (who, when, which record). The full audit report is available to administrators and compliance reviewers.</p>
          </Card>
        )}
      </div>

      <Card className="p-6" data-testid="whats-left-card">
        <div className="flex items-center gap-2 mb-1"><FileCheck className="h-5 w-5 text-brand-700" /><h2 className="font-display text-xl font-bold">Path to full HIPAA compliance</h2></div>
        <p className="text-sm text-muted-foreground mb-4">The technical safeguards are in place. Full compliance is a combination of technology, legal agreements, and operating practices — here is what remains.</p>
        <ol className="space-y-2.5">
          {WHATS_LEFT.map((s, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-foreground" data-testid={`whats-left-${i}`}>
              <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 border-brand-300 text-[11px] font-bold text-brand-700 flex items-center justify-center">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}
